<template>
    <div>
        <rkm-form ref="Form">
            <rkm-row>
                <rkm-col cols="3">
                    <rkm-form-input
                        disabled
                        label="Número de recebimento"
                        v-model="data.model.numeroRecebimento"
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-input
                        :disabled="!data.additionals.permiteEdicao || !data.additionals.protocoloEditavel"
                        :placeholder="!data.model.protocolo && !data.additionals.protocoloEditavel ? 'Gerado ao salvar' : null"
                        :validations="validations.protocolo"
                        label="Protocolo"
                        maxlength="20"
                        v-model="data.model.protocolo"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="3">
                    <rkm-form-date-picker
                        :disabled="!data.additionals.permiteEdicao"
                        :validations="validations.dataSolicitacao"
                        label="Data da solicitação"
                        v-model="data.model.solicitadoEm"
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-select
                        :disabled="!data.additionals.permiteEdicao"
                        :options="data.form.tipos"
                        :validations="validations.tipo"
                        label="Tipo"
                        text-field="nome"
                        v-model="data.model.tipoId"
                        value-field="id"
                        show-default-option
                    />
                </rkm-col>

                <rkm-col cols="2">
                    <rkm-form-select
                        :disabled="!data.additionals.permiteEdicao || subtipos.length === 0"
                        :options="subtipos"
                        :validations="validations.subtipo"
                        label="Subtipo"
                        text-field="nome"
                        v-model="data.model.subtipoId"
                        value-field="id"
                        show-default-option
                    />
                </rkm-col>

                <rkm-col cols="4">
                    <rkm-form-select
                        :disabled="!data.additionals.permiteEdicao"
                        :options="data.form.unidades"
                        :validations="validations.unidadeSolicitante"
                        label="Unidade solicitante"
                        text-field="nomeFantasia"
                        v-model="data.model.unidadeSolicitanteId"
                        value-field="id"
                        show-default-option
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="6">
                    <rkm-form-input-autocomplete
                        :disabled="!data.additionals.permiteEdicao"
                        :repository="repositories.usuario"
                        :request-query-string-params="queryStringParams.usuario"
                        :search-fields="fields.usuario"
                        :validations="validations.usuarioSolicitante"
                        label="Médico solicitante"
                        text-field="cidadaoNome"
                        uppercase
                        v-model="data.model.usuarioSolicitante"
                    >
                        <rkm-input-group-append slot="append">
                            <rkm-button
                                :disabled="!data.additionals.permiteEdicao"
                                @click.stop="$refs.ModalUsuarioSimplificado.open()"
                                color="primary"
                                title="Cadastrar novo usuário"
                            >
                                <rkm-icon icon="plus" />
                            </rkm-button>
                        </rkm-input-group-append>
                    </rkm-form-input-autocomplete>
                </rkm-col>

                <rkm-col cols="6">
                    <rkm-form-input-autocomplete
                        :disabled="!data.additionals.permiteEdicao"
                        :repository="repositories.cid"
                        :search-fields="fields.cid"
                        :validations="validations.cid"
                        label="CID"
                        :text-field="cid => `${formatCid(cid.codigo)} - ${cid.nome}`"
                        uppercase
                        v-model="data.model.cid"
                    >
                        <template v-slot:itemLabel="{ item }">
                            {{ item.codigo | formatCid }} - {{ item.nome }}
                        </template>
                    </rkm-form-input-autocomplete>
                </rkm-col>
            </rkm-row>
        </rkm-form>

        <rkm-area
            title="Devoluções do processo"
            type="fieldset"
        >
            <template v-slot:trailing>
                <rkm-button
                    :disabled="!data.additionals.permiteEdicao || !!formDevolucaoProcesso || (!!ultimaDevolucaoProcesso && !ultimaDevolucaoProcesso.reabertoEm)"
                    @click="formDevolucaoProcesso = { devolvidoEm: null, motivo: null }"
                    color="primary"
                    small
                    title="Incluir devolução"
                >
                    <rkm-icon icon="plus" />
                </rkm-button>
            </template>

            <rkm-form
                v-if="formDevolucaoProcesso"
                ref="FormDevolucaoProcesso"
            >
                <rkm-row>
                    <rkm-col cols="3">
                        <rkm-form-date-picker
                            :max-date="data.additionals.dataAtual"
                            :min-date="ultimaDevolucaoProcesso ? getFormatedDate(ultimaDevolucaoProcesso.reabertoEm, 'DD/MM/YYYY') : null"
                            :validations="validations.devolucaoProcessoData"
                            label="Data"
                            v-model="formDevolucaoProcesso.devolvidoEm"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col>
                        <rkm-form-textarea
                            :validations="validations.devolucaoProcessoMotivo"
                            label="Motivo"
                            v-model="formDevolucaoProcesso.motivo"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row class="mb-2">
                    <rkm-col>
                        <rkm-button
                            @click="$refs.FormDevolucaoProcesso.validate(confirmarDevolucaoProcesso)"
                            color="primary"
                            small
                        >
                            Confirmar
                        </rkm-button>

                        <rkm-button
                            @click="formDevolucaoProcesso = null"
                            small
                        >
                            Cancelar
                        </rkm-button>
                    </rkm-col>
                </rkm-row>
            </rkm-form>

            <rkm-form
                v-if="formReabertura"
                ref="FormReabertura"
            >
                <rkm-row>
                    <rkm-col cols="3">
                        <rkm-form-date-picker
                            :max-date="data.additionals.dataAtual"
                            :min-date="getFormatedDate(ultimaDevolucaoProcesso.devolvidoEm, 'DD/MM/YYYY')"
                            :validations="validations.reabertoEm"
                            label="Data da reabertura"
                            v-model="formReabertura.reabertoEm"
                        />
                        <small class="d-block text-muted">
                            Até hoje, não anterior a {{ ultimaDevolucaoProcesso.devolvidoEm | formatDate }}
                        </small>
                    </rkm-col>
                </rkm-row>

                <rkm-row class="mb-2">
                    <rkm-col>
                        <rkm-button
                            @click="$refs.FormReabertura.validate(confirmarReabertura)"
                            color="primary"
                            small
                        >
                            Confirmar
                        </rkm-button>

                        <rkm-button
                            @click="formReabertura = null"
                            small
                        >
                            Cancelar
                        </rkm-button>
                    </rkm-col>
                </rkm-row>
            </rkm-form>

            <rkm-table
                :fields="fields.devolucoesProcesso"
                :items="data.model.devolucoesProcesso"
                hide-filters
                hide-pagination
            >
                <template v-slot:cell(devolvidoEm)="{ item }">
                    {{ item.devolvidoEm | formatDate }}
                    <small class="d-block text-muted">{{ item.registro || 'Registrado ao salvar' }}</small>
                </template>

                <template v-slot:cell(reabertoEm)="{ index, item }">
                    <template v-if="item.reabertoEm">
                        {{ item.reabertoEm | formatDate }}
                        <small class="d-block text-muted">{{ item.reaberturaRegistro || 'Registrado ao salvar' }}</small>
                    </template>

                    <rkm-button
                        v-else-if="index === 0 && data.additionals.permiteEdicao"
                        :disabled="!!formReabertura"
                        @click="formReabertura = { reabertoEm: null }"
                        color="primary"
                        small
                    >
                        <rkm-icon icon="redo" /> Reabrir processo
                    </rkm-button>
                </template>
            </rkm-table>
        </rkm-area>

        <rkm-area
            title="Devoluções de medicamento"
            type="fieldset"
        >
            <template v-slot:trailing>
                <rkm-button
                    :disabled="!data.additionals.permiteEdicao || !!formDevolucaoMedicamento || data.model.itens.length === 0"
                    @click="abrirDevolucaoMedicamento"
                    color="primary"
                    small
                    title="Incluir devolução de medicamento"
                >
                    <rkm-icon icon="plus" />
                </rkm-button>
            </template>

            <rkm-form
                v-if="formDevolucaoMedicamento"
                ref="FormDevolucaoMedicamento"
            >
                <rkm-row>
                    <rkm-col cols="3">
                        <rkm-form-date-picker
                            :max-date="data.additionals.dataAtual"
                            :validations="validations.devolucaoMedicamentoData"
                            label="Data"
                            v-model="formDevolucaoMedicamento.devolvidoEm"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="data.model.itens.map(i => i.material)"
                            :validations="validations.devolucaoMedicamentoMaterial"
                            label="Medicamento"
                            show-default-option
                            text-field="descricaoCompleta"
                            v-model="formDevolucaoMedicamento.materialId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-input
                            :validations="validations.devolucaoMedicamentoQuantidade"
                            label="Quantidade"
                            step=".001"
                            type="number"
                            v-model="formDevolucaoMedicamento.quantidade"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="3">
                        <rkm-form-input
                            :validations="validations.devolucaoMedicamentoLote"
                            label="Lote"
                            maxlength="15"
                            v-model="formDevolucaoMedicamento.lote"
                        />
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-date-picker
                            label="Data de fabricação"
                            v-model="formDevolucaoMedicamento.dataFabricacao"
                        />
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-date-picker
                            :min-date="formDevolucaoMedicamento.dataFabricacao ? getFormatedDate(formDevolucaoMedicamento.dataFabricacao, 'DD/MM/YYYY') : null"
                            :validations="validations.devolucaoMedicamentoDataValidade"
                            label="Data de validade"
                            v-model="formDevolucaoMedicamento.dataValidade"
                        />
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-input
                            :validations="validations.devolucaoMedicamentoLaboratorio"
                            label="Laboratório"
                            maxlength="100"
                            v-model="formDevolucaoMedicamento.laboratorio"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col>
                        <rkm-form-textarea
                            :validations="validations.devolucaoMedicamentoMotivo"
                            label="Motivo"
                            v-model="formDevolucaoMedicamento.motivo"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row class="mb-2">
                    <rkm-col>
                        <rkm-button
                            @click="$refs.FormDevolucaoMedicamento.validate(incluirDevolucaoMedicamento)"
                            color="primary"
                            small
                        >
                            Incluir
                        </rkm-button>

                        <rkm-button
                            @click="formDevolucaoMedicamento = null"
                            small
                        >
                            Cancelar
                        </rkm-button>
                    </rkm-col>
                </rkm-row>
            </rkm-form>

            <rkm-table
                :fields="fields.devolucoesMedicamento"
                :items="data.model.devolucoesMedicamento"
                hide-filters
                hide-pagination
            >
                <template v-slot:cell(devolvidoEm)="{ item }">
                    {{ item.devolvidoEm | formatDate }}
                </template>

                <template v-slot:cell(material)="{ item }">
                    {{ item.material ? item.material.descricaoCompleta : '—' }}
                </template>

                <template v-slot:cell(dataValidade)="{ item }">
                    <template v-if="item.dataValidade">
                        {{ item.dataValidade | formatDate }}
                    </template>
                </template>

                <template v-slot:cell(registro)="{ item }">
                    {{ item.registro || 'Registrado ao salvar' }}
                </template>
            </rkm-table>

            <small>Não movimenta estoque. A data de validade não pode ser anterior à de fabricação.</small>
        </rkm-area>

        <rkm-form ref="FormObservacoes">
            <rkm-area title="Observações">
                <rkm-row>
                    <rkm-col>
                        <rkm-form-textarea
                            :disabled="!data.additionals.permiteEdicao"
                            :validations="validations.observacoes"
                            v-model="data.model.observacoes"
                        />
                    </rkm-col>
                </rkm-row>
            </rkm-area>

            <rkm-area title="Observações DRS">
                <rkm-row>
                    <rkm-col>
                        <rkm-form-textarea
                            :disabled="!data.additionals.permiteEdicao"
                            :validations="validations.observacoesDrs"
                            v-model="data.model.observacoesDrs"
                        />
                    </rkm-col>
                </rkm-row>
            </rkm-area>
        </rkm-form>

        <ModalUsuarioSimplificado
            ref="ModalUsuarioSimplificado"
            @save="setUsuarioSolicitante"
        />
    </div>
</template>

<script>
// Components.
import ModalUsuarioSimplificado from '@/components/cadastro/usuario/components/cadastro-simplificado/Modal'

// Others.
import formatsFilter from '@/filters/formats'
import { repository, formats, data } from '@/mixins'

export default {
    components: { ModalUsuarioSimplificado },
    computed: {
        queryStringParams() {

            return {
                usuario: { with_not_regularized: true },
            }
        },
        fields() {

            return {
                usuario:  [
                    { key: 'cadastroCidadao.nome', searchable: true },
                    { key: 'cadastroUsuario.registroNumero', searchable: true },
                ],
                cid:  [
                    { key: 'codigo', searchable: true },
                    { key: 'nome', searchable: true },
                ],
                devolucoesProcesso: [
                    { key: 'devolvidoEm', label: 'Data', class: 'col-min text-nowrap' },
                    { key: 'motivo', label: 'Motivo' },
                    { key: 'reabertoEm', label: 'Data da reabertura', class: 'col-min text-nowrap' },
                ],
                devolucoesMedicamento: [
                    { key: 'devolvidoEm', label: 'Data', class: 'col-min text-nowrap' },
                    { key: 'material', label: 'Medicamento' },
                    { key: 'quantidade', label: 'Qtd.', class: 'col-min' },
                    { key: 'lote', label: 'Lote', class: 'col-min' },
                    { key: 'dataValidade', label: 'Validade', class: 'col-min text-nowrap' },
                    { key: 'registro', label: 'Registro', class: 'col-min text-nowrap' },
                ],
            }
        },
        validations() {

            return {
                dataSolicitacao: { required: true },
                unidadeSolicitante: { required: true },
                usuarioSolicitante: { required: true },
                tipo: { required: true },
                cid: {},
                protocolo: { max: 20 },
                observacoes: { max: 5000 },
                observacoesDrs: { max: 5000 },
                devolucaoProcessoData: { required: true },
                devolucaoProcessoMotivo: { required: true, max: 5000 },
                reabertoEm: { required: true },
                devolucaoMedicamentoData: { required: true },
                devolucaoMedicamentoMaterial: { required: true },
                devolucaoMedicamentoQuantidade: { required: true, min_value: 0.001, max_value: 999.999 },
                devolucaoMedicamentoLote: { required: true, max: 15 },
                devolucaoMedicamentoDataValidade: { required: true },
                devolucaoMedicamentoLaboratorio: { max: 100 },
                devolucaoMedicamentoMotivo: { max: 5000 },
            }
        },
        ultimaDevolucaoProcesso() {

            return this.data.model.devolucoesProcesso[0] || null
        },
        subtipos() {

            return this.data.form.subtipos.filter(s => s.tipoId === this.data.model.tipoId)
        }
    },
    created() {

        this.repositories = {
            usuario: this.getConectaSusRepository('cadastro.sujeitoAtencao.usuario'),
            cid: this.getConectaSusRepository('sigtap.cid'),
        }
    },
    data: () => ({
        formDevolucaoProcesso: null,
        formReabertura: null,
        formDevolucaoMedicamento: null,
    }),
    filters: {
        ...formatsFilter,
    },
    methods: {
        async validate() {

            return await this.$refs.Form.validate() && await this.$refs.FormObservacoes.validate()
        },
        confirmarDevolucaoProcesso() {

            this.data.model.devolucoesProcesso.unshift({
                id: null,
                devolvidoEm: this.getFormatedDate(this.formDevolucaoProcesso.devolvidoEm),
                motivo: this.formDevolucaoProcesso.motivo,
                reabertoEm: null,
                registro: null,
                reaberturaRegistro: null,
            })

            this.formDevolucaoProcesso = null
        },
        confirmarReabertura() {

            this.ultimaDevolucaoProcesso.reabertoEm = this.getFormatedDate(this.formReabertura.reabertoEm)

            this.formReabertura = null
        },
        abrirDevolucaoMedicamento() {

            this.formDevolucaoMedicamento = {
                devolvidoEm: null,
                materialId: null,
                quantidade: null,
                lote: null,
                dataFabricacao: null,
                dataValidade: null,
                laboratorio: null,
                motivo: null,
            }
        },
        incluirDevolucaoMedicamento() {

            const f = this.formDevolucaoMedicamento

            this.data.model.devolucoesMedicamento.unshift({
                id: null,
                devolvidoEm: this.getFormatedDate(f.devolvidoEm),
                material: this.data.model.itens.find(i => i.material.id === f.materialId).material,
                quantidade: f.quantidade,
                lote: f.lote,
                dataFabricacao: this.getFormatedDate(f.dataFabricacao),
                dataValidade: this.getFormatedDate(f.dataValidade),
                laboratorio: f.laboratorio,
                motivo: f.motivo,
                registro: null,
            })

            this.formDevolucaoMedicamento = null
        },
        setUsuarioSolicitante(usuario) {

            this.data.model.usuarioSolicitante = {
                id: usuario.id,
                cidadaoNome: usuario.cidadao.nome
            }
        }
    },
    mixins: [repository, formats, data],
    props: {
        data: { type: Object, required: true },
    },
    watch: {
        subtipos(subtipos) {

            if (subtipos.length === 0) {

                this.data.model.subtipoId = null
            }
        },
    }
}
</script>